import React from 'react';
import { Field, change, type WrappedFieldProps } from 'redux-form';
import { ConfigProvider, Flex, Popconfirm, theme as antdTheme } from 'antd';
import { useAdminDispatch, useAdminSelector } from 'admin/reducers';
import DesignSystemEditor, { designDefinitionError } from 'admin/components/common/DesignSystemEditor';
import { antdThemeDark } from 'admin/components/common/Theme';
import BrandKitSelect from 'admin/components/common/BrandKitSelect';
import DashboardButton from 'admin/components/common/Button';
import Text from 'admin/components/common/Text';
import Tooltip from 'admin/components/common/Tooltip';
import t from 'utils/translate';
import { selectActiveOrganization } from 'admin/reducers/user/selectors';
import {
	areDesignDefinitionsEqual,
	copyDesignDefinition,
	createDesignDefinition,
	SYSTEM_DEFAULT_FONT_FAMILY,
} from 'common/design-tokens';
import { getBrandKitById } from 'common/brand-kit/projection';
import { SYSTEM_FONTS } from 'common/constants';
import { canonicalFontFamily } from 'admin/components/common/AI/Chat/fonts/font-resolver';
import type { SettingsTabsGeneralProps } from '../types';
import css from './Tokens.scss';

const storyTokensTheme = {
	...antdThemeDark,
	algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm],
};

function DesignField({
	input,
	meta,
	disabled,
	fonts,
	theme,
}: WrappedFieldProps & Omit<React.ComponentProps<typeof DesignSystemEditor>, 'value' | 'onChange'>) {
	return (
		<DesignSystemEditor
			value={input.value || undefined}
			onChange={input.onChange}
			disabled={disabled || meta.submitting}
			fonts={fonts}
			fontContext="story"
			theme={theme}
		/>
	);
}

export default function Tokens(props: SettingsTabsGeneralProps) {
	const { fields, version, formInfo, formValues } = props;
	const organization = useAdminSelector(selectActiveOrganization);
	const fonts = [
		{ fontFamily: SYSTEM_DEFAULT_FONT_FAMILY },
		...Object.values({
			...SYSTEM_FONTS,
			...formValues.storyVersions?.[version]?.settings.fonts,
		}).filter(Boolean),
	];
	const dispatch = useAdminDispatch();
	const selectedBrandKitId = formValues.storyVersions?.[version]?.settings.brandKitId ?? '';
	const selectedBrandKit = getBrandKitById(organization?.metadata?.brandKits, selectedBrandKitId);
	const currentDesign = formValues.storyVersions?.[version]?.settings.design ?? createDesignDefinition();
	const designMatchesBrandKit =
		!!selectedBrandKit && areDesignDefinitionsEqual(currentDesign, selectedBrandKit.design);
	const resetDisabledReason = !selectedBrandKit
		? t('designTokens.resetNoBrandKit')
		: designMatchesBrandKit
			? t('designTokens.resetAlreadyMatches')
			: undefined;
	const reset = () => {
		if (!selectedBrandKit) return;
		dispatch(change(formInfo.name, fields.design.name(version), copyDesignDefinition(selectedBrandKit.design)));
		const defaultFontFamily = selectedBrandKit.design.defaults.root.fontFamily;
		const organizationFonts = Object.entries(organization?.metadata?.fonts ?? {}).filter(
			([, font]) => canonicalFontFamily(font.fontFamily) === canonicalFontFamily(defaultFontFamily)
		);
		if (organizationFonts.length) {
			dispatch(
				change(formInfo.name, fields.fonts.name(version), {
					...(formValues.storyVersions?.[version]?.settings.fonts ?? {}),
					...Object.fromEntries(organizationFonts),
				})
			);
		}
	};
	const changeBrandKit = (brandKitId: string) => {
		dispatch(change(formInfo.name, fields.brandKitId.name(version), brandKitId || null));
	};
	const resetButton = (
		<Popconfirm
			disabled={!!resetDisabledReason || formInfo.submitting}
			title={t('designTokens.resetConfirm')}
			onConfirm={reset}
		>
			<DashboardButton
				theme="dark"
				view="secondary-danger"
				color="danger"
				size="medium"
				disabled={formInfo.submitting || !!resetDisabledReason}
			>
				{t('designTokens.reset')}
			</DashboardButton>
		</Popconfirm>
	);
	return (
		<ConfigProvider theme={storyTokensTheme}>
			<div className={css.tokens}>
				<header className={css.heading}>
					<Text
						tag="h1"
						className={css.title}
						size={Text.size.subheading}
						weight={Text.weight.semibold}
						compact
					>
						{t('designTokens.title')}
					</Text>
					<Text
						className={css.description}
						size={Text.size.body}
						text="Default and reusable style values shared by every card in this story. Changes here don't affect the Brand Kit."
						compact
					/>
				</header>
				<Field
					name={fields.design.name(version)}
					component={DesignField}
					fonts={fonts}
					theme="dark"
					validate={designDefinitionError}
				/>
				<Flex vertical gap="large" aria-labelledby="story-token-brand-kit-heading" style={{ marginBlock: 40 }}>
					<Flex vertical gap="large">
						<Text tag="h2" id="story-token-brand-kit-heading" size={Text.size.article}>
							Brand Kit
						</Text>
						<Text className={css.brandKitDescription} size={Text.size.label}>
							• The selected Brand Kit supplies assets, links, and guidelines to AI.
							<br />• Reset replaces this Story&apos;s design defaults and tokens with this kit&apos;s
							design.
						</Text>
					</Flex>
					<div className={css.brandKitRow}>
						<BrandKitSelect
							className={css.brandKitSelect}
							id="story-token-brand-kit"
							ariaLabel="Brand Kit context"
							theme="dark"
							kits={organization?.metadata?.brandKits ?? []}
							value={selectedBrandKitId}
							includeNoBrandKit
							onChange={changeBrandKit}
							menuPlacement="top"
						/>
						{resetDisabledReason ? (
							<Tooltip isStatic stylePreset="dark-2" placement="top" content={resetDisabledReason}>
								{resetButton}
							</Tooltip>
						) : (
							resetButton
						)}
					</div>
				</Flex>
			</div>
		</ConfigProvider>
	);
}
